import { SITE_CONFIG } from '../config/site';
import { AppView, ViewParams } from '../context/AppContext';

export interface PageMetadata {
  title: string;
  description: string;
  ogDescription?: string;
  canonicalPath: string;
  noindex?: boolean;
  ogType?: 'website' | 'article' | 'profile';
  ogImage?: string;
  ogImageAlt?: string;
  category?: string;
}

export interface ResolvedPageSEO extends PageMetadata {
  canonicalUrl: string;
  socialDescription: string;
  isNoIndex: boolean;
}

export const SERVICE_PAGES_METADATA: Record<string, PageMetadata> = {
  'auto-mechanic': {
    title: 'Auto Mechanic in Nigeria – Hire Verified Auto Mechanics | JobMatch NG',
    description:
      'Hire NIN-verified auto mechanics across Nigeria for computerized OBD engine diagnostics, brake servicing, suspension fixes, transmission repair, and routine vehicle maintenance on JobMatch NG.',
    ogDescription:
      'Book verified Nigerian auto mechanics for computerized OBD diagnostics, brake repairs, suspension servicing, and engine maintenance on JobMatch NG.',
    canonicalPath: '/services/auto-mechanic',
    noindex: false,
    category: 'Auto Mechanic',
  },
  plumber: {
    title: 'Plumber in Nigeria – Hire Verified Plumbers | JobMatch NG',
    description:
      'Find trusted plumbers in Nigeria for burst pipe repairs, borehole submersible pump installation, water heater servicing, bathroom fittings, and drainage unblocking with upfront quotes on JobMatch NG.',
    ogDescription:
      'Connect with verified plumbers across Nigeria for leak detection, borehole pumps, water heaters, and bathroom plumbing with upfront Naira quotes on JobMatch NG.',
    canonicalPath: '/services/plumber',
    noindex: false,
    category: 'Plumber',
  },
  electrician: {
    title: 'Electrician in Nigeria – Hire Verified Electricians | JobMatch NG',
    description:
      'Connect with certified electricians across Nigeria for house conduit wiring, manual and automatic changeover switches, distribution board repairs, and earth-leakage fault diagnosis on JobMatch NG.',
    ogDescription:
      'Hire certified electricians in Nigeria for conduit wiring, changeover switches, distribution boards, and electrical fault repairs on JobMatch NG.',
    canonicalPath: '/services/electrician',
    noindex: false,
    category: 'Electrician',
  },
  carpenter: {
    title: 'Carpenter in Nigeria – Hire Verified Carpenters | JobMatch NG',
    description:
      'Hire skilled carpenters in Nigeria for timber roof trusses, flush door installation, custom kitchen cabinetry, wardrobes, and woodwork repairs with itemized Naira quotes on JobMatch NG.',
    ogDescription:
      'Find verified Nigerian carpenters for roofing timber trusses, doors, kitchen cabinets, wardrobes, and custom woodwork on JobMatch NG.',
    canonicalPath: '/services/carpenter',
    noindex: false,
    category: 'Carpenter',
  },
  painter: {
    title: 'Painter in Nigeria – Hire Verified Painters | JobMatch NG',
    description:
      'Book professional house and commercial painters in Nigeria for wall screeding, POP primer, emulsion and gloss coats, anti-damp treatment, and weather-shield exterior finishes on JobMatch NG.',
    ogDescription:
      'Hire vetted house and commercial painters in Nigeria for wall screeding, interior emulsion, anti-damp treatment, and exterior finishes on JobMatch NG.',
    canonicalPath: '/services/painter',
    noindex: false,
    category: 'Painter',
  },
  'ac-technician': {
    title: 'AC Technician in Nigeria – Hire Verified AC Technicians | JobMatch NG',
    description:
      'Find verified air conditioner technicians in Nigeria for split and inverter AC installation, deep chemical coil washing, R410/R22 gas refilling, copper piping, and compressor repairs on JobMatch NG.',
    ogDescription:
      'Book certified AC technicians across Nigeria for split and inverter air conditioner installation, gas charging, servicing, and repairs on JobMatch NG.',
    canonicalPath: '/services/ac-technician',
    noindex: false,
    category: 'AC Technician',
  },
  'generator-technician': {
    title: 'Generator Technician in Nigeria – Hire Verified Generator Mechanics | JobMatch NG',
    description:
      'Hire experienced diesel and petrol generator technicians in Nigeria for Mikano, Perkins, Sumec Firman, and Elepaq servicing, AVR replacement, carburetor tuning, and ATS hookups on JobMatch NG.',
    ogDescription:
      'Connect with verified diesel and petrol generator mechanics in Nigeria for plant servicing, AVR repairs, and ATS installation on JobMatch NG.',
    canonicalPath: '/services/generator-technician',
    noindex: false,
    category: 'Generator Technician',
  },
  'solar-technician': {
    title: 'Solar Technician in Nigeria – Hire Verified Solar & Inverter Installers | JobMatch NG',
    description:
      'Connect with vetted solar and inverter technicians across Nigeria for roof solar panel mounting, hybrid inverter installation, lithium battery bank wiring, and MPPT charge controller setup on JobMatch NG.',
    ogDescription:
      'Hire verified solar and inverter installers in Nigeria for solar panels, hybrid inverters, and lithium battery backup systems on JobMatch NG.',
    canonicalPath: '/services/solar-technician',
    noindex: false,
    category: 'Solar Technician',
  },
  welder: {
    title: 'Welder in Nigeria – Hire Verified Welders & Metal Fabricators | JobMatch NG',
    description:
      'Find skilled welders and metal fabricators in Nigeria for security iron gates, burglary-proof window grilles, overhead water tank stands, steel roof trusses, and handrails on JobMatch NG.',
    ogDescription:
      'Hire verified welders and metal fabricators across Nigeria for security gates, burglary proofs, tank stands, and structural steelwork on JobMatch NG.',
    canonicalPath: '/services/welder',
    noindex: false,
    category: 'Welder',
  },
  tiler: {
    title: 'Tiler in Nigeria – Hire Verified Floor & Wall Tilers | JobMatch NG',
    description:
      'Hire precision floor and wall tilers in Nigeria for vitrified porcelain tiles, bathroom waterproof tiling, kitchen granite countertops, marble slabs, and interlocking paving stones on JobMatch NG.',
    ogDescription:
      'Book experienced floor and wall tilers in Nigeria for porcelain tiles, bathroom tiling, granite slabs, and interlocking stones on JobMatch NG.',
    canonicalPath: '/services/tiler',
    noindex: false,
    category: 'Tiler',
  },
  bricklayer: {
    title: 'Bricklayer in Nigeria – Hire Verified Bricklayers & Masons | JobMatch NG',
    description:
      'Find experienced bricklayers and masons in Nigeria for blocklaying, boundary fence construction, cement plastering, soakaway pits, and German floor casting on JobMatch NG.',
    ogDescription:
      'Connect with verified bricklayers and masons across Nigeria for blockwork, plastering, fence construction, and concrete flooring on JobMatch NG.',
    canonicalPath: '/services/bricklayer',
    noindex: false,
    category: 'Bricklayer',
  },
  'furniture-maker': {
    title: 'Furniture Maker in Nigeria – Hire Verified Furniture Makers | JobMatch NG',
    description:
      'Connect with custom furniture makers and upholsterers in Nigeria for handcrafted sofas, dining sets, solid wood bed frames, executive office desks, and leather re-upholstery on JobMatch NG.',
    ogDescription:
      'Hire skilled Nigerian furniture makers and upholsterers for custom sofas, beds, dining tables, office furniture, and restoration on JobMatch NG.',
    canonicalPath: '/services/furniture-maker',
    noindex: false,
    category: 'Furniture Maker',
  },
  'cctv-security-installer': {
    title: 'CCTV & Security Installer in Nigeria – Hire Verified Technicians | JobMatch NG',
    description:
      'Hire verified CCTV and electronic security installers across Nigeria for IP surveillance cameras, perimeter electric fencing, automatic gate motors, and video intercom systems on JobMatch NG.',
    ogDescription:
      'Book verified CCTV and security system installers in Nigeria for surveillance cameras, electric fences, and access control on JobMatch NG.',
    canonicalPath: '/services/cctv-security-installer',
    noindex: false,
    category: 'CCTV & Security Installer',
  },
  cleaner: {
    title: 'Cleaner & Fumigation in Nigeria – Hire Verified Cleaning Pros | JobMatch NG',
    description:
      'Book vetted cleaning and fumigation professionals in Nigeria for post-construction deep scrubbing, move-in residential cleaning, upholstery steam washing, and licensed pest control on JobMatch NG.',
    ogDescription:
      'Hire verified residential and post-construction cleaners and fumigation specialists across Nigeria on JobMatch NG.',
    canonicalPath: '/services/cleaner',
    noindex: false,
    category: 'Cleaner & Fumigation',
  },
  'pop-ceiling-installer': {
    title: 'POP & Ceiling Installer in Nigeria – Hire Verified Installers | JobMatch NG',
    description:
      'Find skilled POP and ceiling installers in Nigeria for decorative plasterboard false ceilings, gypsum drywall partitions, cornice mouldings, and PVC ceiling panels on JobMatch NG.',
    ogDescription:
      'Connect with verified POP and false ceiling installers in Nigeria for plasterboard designs, drywall partitions, and PVC ceilings on JobMatch NG.',
    canonicalPath: '/services/pop-ceiling-installer',
    noindex: false,
    category: 'POP & Ceiling Installer',
  },
  roofer: {
    title: 'Roofer in Nigeria – Hire Verified Roofing Specialists | JobMatch NG',
    description:
      'Hire experienced roofers in Nigeria for stone-coated step-tile roofing, corrugated aluminium sheets, emergency roof leak sealing, and rain gutter installation on JobMatch NG.',
    ogDescription:
      'Hire verified roofing specialists across Nigeria for Gerard stone-coated tiles, aluminium roofing, leak repairs, and rain gutters on JobMatch NG.',
    canonicalPath: '/services/roofer',
    noindex: false,
    category: 'Roofer',
  },
  'aluminum-glass-installer': {
    title: 'Aluminum & Glass Installer in Nigeria – Hire Verified Glaziers | JobMatch NG',
    description:
      'Connect with aluminium and glass installers in Nigeria for casement and sliding windows, frameless glass shower cubicles, office partitions, and mosquito net frames on JobMatch NG.',
    ogDescription:
      'Book verified aluminium fabricators and glaziers in Nigeria for casement windows, glass shower cubicles, and office partitions on JobMatch NG.',
    canonicalPath: '/services/aluminum-glass-installer',
    noindex: false,
    category: 'Aluminum & Glass Installer',
  },
  locksmith: {
    title: 'Locksmith in Nigeria – Hire Verified Locksmiths | JobMatch NG',
    description:
      'Find trusted locksmiths in Nigeria for emergency door unlocking, smart biometric lock installation, deadbolt replacement, master key systems, and broken key extraction on JobMatch NG.',
    ogDescription:
      'Connect with verified locksmiths across Nigeria for emergency lockouts, smart door locks, and security cylinder replacements on JobMatch NG.',
    canonicalPath: '/services/locksmith',
    noindex: false,
    category: 'Locksmith',
  },
  'appliance-repair': {
    title: 'Appliance Repair in Nigeria – Hire Verified Appliance Technicians | JobMatch NG',
    description:
      'Hire verified home appliance repair technicians in Nigeria for automatic washing machines, microwaves, electric ovens, gas cookers, and water dispensers on JobMatch NG.',
    ogDescription:
      'Find verified appliance repair technicians in Nigeria for washing machines, cookers, microwaves, and home appliances on JobMatch NG.',
    canonicalPath: '/services/appliance-repair',
    noindex: false,
    category: 'Appliance Repair',
  },
  'landscaper-gardener': {
    title: 'Landscaper & Gardener in Nigeria – Hire Verified Gardeners | JobMatch NG',
    description:
      'Find professional landscapers and gardeners in Nigeria for compound lawn mowing, hedge trimming, artificial turf laying, flower bed design, and tree pruning on JobMatch NG.',
    ogDescription:
      'Book verified landscapers and gardeners across Nigeria for lawn care, hedge trimming, interlocking gardens, and turf installation on JobMatch NG.',
    canonicalPath: '/services/landscaper-gardener',
    noindex: false,
    category: 'Landscaper & Gardener',
  },
  'phone-computer-technician': {
    title: 'Phone & Computer Technician in Nigeria – Hire Verified Repair Pros | JobMatch NG',
    description:
      'Connect with skilled phone and laptop repair technicians in Nigeria for screen replacement, motherboard diagnostics, charging port fixes, battery replacement, and data recovery on JobMatch NG.',
    ogDescription:
      'Hire verified phone and laptop repair technicians in Nigeria for screen replacement, board repairs, and hardware diagnostics on JobMatch NG.',
    canonicalPath: '/services/phone-computer-technician',
    noindex: false,
    category: 'Phone & Computer Technician',
  },
  'refrigeration-technician': {
    title: 'Refrigeration Technician in Nigeria – Hire Verified Fridge & Chiller Pros | JobMatch NG',
    description:
      'Hire specialized refrigeration technicians in Nigeria for domestic refrigerators, commercial deep freezers, cold rooms, compressor replacement, and gas refilling on JobMatch NG.',
    ogDescription:
      'Connect with verified refrigeration and cold-room technicians across Nigeria for fridge repairs, deep freezers, and compressor servicing on JobMatch NG.',
    canonicalPath: '/services/refrigeration-technician',
    noindex: false,
    category: 'Refrigeration Technician',
  },
  'moving-relocation-services': {
    title: 'Moving & Relocation Services in Nigeria – Hire Verified Movers | JobMatch NG',
    description:
      'Book reliable home and office relocation professionals in Nigeria for careful packing, furniture dismantling and reassembly, and local or interstate haulage on JobMatch NG.',
    ogDescription:
      'Hire verified moving and relocation specialists in Nigeria for residential packing, furniture assembly, and interstate transport on JobMatch NG.',
    canonicalPath: '/services/moving-relocation-services',
    noindex: false,
    category: 'Moving & Relocation Services',
  },
};

export const PUBLIC_PAGES_METADATA: Record<string, PageMetadata> = {
  home: {
    title: 'JobMatch NG | Verified Artisans & Technicians in Nigeria',
    description:
      'Hire verified plumbers, electricians, AC technicians, generator mechanics, and skilled artisans across Nigeria. Post repair jobs and compare quotes in Naira.',
    ogDescription:
      'Hire verified plumbers, electricians, AC technicians, generator mechanics, and skilled artisans across Nigeria with JobMatch NG.',
    canonicalPath: '/',
    noindex: false,
    ogType: 'website',
    ogImage: 'https://jobmatch.ng/og-image.png',
    ogImageAlt: 'JobMatch NG connects customers with skilled professionals across Nigeria.',
  },
  find_pro: {
    title: 'Find Verified Artisans & Technicians in Nigeria | JobMatch NG',
    description:
      'Search NIN-verified Nigerian skilled craftspeople, plumbers, electricians, mechanics, AC technicians, and solar installers by trade and LGA across 36 states and Abuja FCT.',
    ogDescription:
      'Browse and compare NIN-verified Nigerian artisans, plumbers, electricians, and technicians by trade and LGA on JobMatch NG.',
    canonicalPath: '/find-a-professional',
    noindex: false,
  },
  post_job: {
    title: 'Post a Service Request | JobMatch NG',
    description:
      'Describe your repair or installation project, get an AI scope diagnosis, and receive quotes from verified professionals in Nigeria.',
    ogDescription:
      'Post your home or commercial repair job on JobMatch NG and receive itemized Naira quotes from verified local professionals.',
    canonicalPath: '/post-job',
    noindex: false,
  },
  how_it_works: {
    title: 'How JobMatch NG Works – Post Jobs & Hire Verified Artisans',
    description:
      'Understand our transparent process: describe your repair or get an AI scope diagnosis, compare itemized quotes in Naira, hire vetted local artisans, and pay safely via Paystack escrow.',
    ogDescription:
      'See how JobMatch NG works: post a repair request, compare itemized Naira quotes from verified artisans, and pay safely with Paystack escrow.',
    canonicalPath: '/how-it-works',
    noindex: false,
  },
  become_pro: {
    title: 'Become a Professional Artisan – Join JobMatch NG Network',
    description:
      'Join Nigeria’s verified trade network. Complete 4-step identity verification, receive real-time local job alerts in your LGA, enjoy ₦0 monthly subscription, and grow your service business.',
    ogDescription:
      'Register as a verified artisan on JobMatch NG to receive matched local customer jobs across Nigeria with ₦0 monthly subscription.',
    canonicalPath: '/become-a-professional',
    noindex: false,
  },
  customer_guide: {
    title: 'For Customers – Guide to Hiring Verified Artisans in Nigeria | JobMatch NG',
    description:
      'Learn how Nigerian homeowners, tenants, and facility managers hire verified professionals safely, compare itemized quotes, inspect completed work, and pay securely via escrow.',
    ogDescription:
      'Complete customer guide to hiring vetted artisans, comparing transparent quotes, and using Paystack escrow protection on JobMatch NG.',
    canonicalPath: '/for-customers',
    noindex: false,
  },
  verification: {
    title: 'Artisan Trust & 4-Step Verification Policy | JobMatch NG',
    description:
      'Learn how JobMatch NG vets independent professionals through Government NIN identity, utility address proof, operational LGA validation, and real-time facial liveness checks.',
    ogDescription:
      'Explore JobMatch NG’s 4-step artisan verification process covering NIN identity, address proof, LGA validation, and biometric liveness.',
    canonicalPath: '/verification',
    noindex: false,
  },
  faq: {
    title: 'Frequently Asked Questions – Hiring & Artisan Network | JobMatch NG',
    description:
      'Find answers to common questions about posting jobs free, 4-step artisan verification, Paystack escrow protection, 10% platform commission, and Telegram AI assistance on JobMatch NG.',
    ogDescription:
      'Answers to frequently asked questions about hiring verified artisans, escrow payments, and professional verification on JobMatch NG.',
    canonicalPath: '/faq',
    noindex: false,
  },
  about: {
    title: 'About JobMatch NG – Nigeria’s Trusted Service Marketplace',
    description:
      'JobMatch NG is an AI-powered Nigerian service marketplace connecting households, property managers, and businesses with vetted independent artisans and technicians across Nigeria.',
    ogDescription:
      'Learn how JobMatch NG connects Nigerian homes and businesses with verified local tradespeople and technicians across all 774 LGAs.',
    canonicalPath: '/about',
    noindex: false,
  },
  contact: {
    title: 'Contact JobMatch NG – Customer & Artisan Support Nigeria',
    description:
      'Contact JobMatch NG support for help with job requests, artisan verification, or escrow payments via support@jobmatch.ng or our Telegram AI Assistant @JobMatchNG_AIAssistant_bot.',
    ogDescription:
      'Get in touch with JobMatch NG customer and artisan support for assistance with jobs, verification, and escrow payments in Nigeria.',
    canonicalPath: '/contact',
    noindex: false,
  },
  privacy: {
    title: 'Privacy Policy (NDPA 2023 Compliant) | JobMatch NG',
    description:
      'Read the JobMatch NG Privacy Policy detailing our compliance with the Nigeria Data Protection Act (NDPA 2023), biometric verification security, and personal data protection.',
    ogDescription:
      'Review JobMatch NG’s NDPA 2023 compliant privacy policy covering customer data protection and encrypted KYC verification.',
    canonicalPath: '/privacy',
    noindex: false,
  },
  terms: {
    title: 'Terms & Conditions of Service | JobMatch NG',
    description:
      'Review the Terms and Conditions governing customer job requests, independent professional quotes, Paystack milestone escrow, and dispute resolution on JobMatch NG.',
    ogDescription:
      'Official Terms and Conditions for customers and verified professionals using the JobMatch NG service marketplace.',
    canonicalPath: '/terms',
    noindex: false,
  },
  customer_auth: {
    title: 'Customer Sign In | JobMatch NG',
    description:
      'Sign in to your JobMatch NG customer account to post service requests, compare quotes, and manage active jobs.',
    ogDescription:
      'Sign in to your JobMatch NG customer portal to manage repair jobs, artisan quotes, and escrow payments.',
    canonicalPath: '/customer-login',
    noindex: false,
  },
  customer_register: {
    title: 'Customer Registration – Create an Account | JobMatch NG',
    description:
      'Create a free customer account on JobMatch NG to hire verified electricians, plumbers, AC technicians, and skilled professionals in Nigeria.',
    ogDescription:
      'Sign up for a free JobMatch NG customer account to post repair jobs and hire verified Nigerian artisans.',
    canonicalPath: '/customer-register',
    noindex: false,
  },
  pro_auth: {
    title: 'Professional Sign In | JobMatch NG',
    description:
      'Sign in to your JobMatch NG professional account to view matched job leads, submit quotes, and manage your earnings.',
    ogDescription:
      'Sign in to the JobMatch NG artisan portal to respond to customer job leads and manage your service earnings.',
    canonicalPath: '/pro-login',
    noindex: false,
  },
  pro_register: {
    title: 'Professional Registration – Join as a Verified Artisan | JobMatch NG',
    description:
      'Register as an independent skilled professional on JobMatch NG to receive matched service jobs in your Local Government Area across Nigeria.',
    ogDescription:
      'Apply as a verified professional artisan on JobMatch NG and receive matched job leads in your LGA across Nigeria.',
    canonicalPath: '/register-pro',
    noindex: false,
  },
  job_details: {
    title: 'Service Request Details | JobMatch NG',
    description:
      'View service request details, trade requirements, location, and verified artisan quotes on JobMatch NG.',
    ogDescription:
      'Review job requirements and submit quote proposals on JobMatch NG.',
    canonicalPath: '/job',
    noindex: false,
  },
};

export const PRIVATE_PAGES_METADATA: Record<string, Partial<PageMetadata>> = {
  customer_dashboard: {
    title: 'Customer Dashboard | JobMatch NG',
    description:
      'Manage your service requests, review artisan quotes, track active jobs, and release escrow payments on JobMatch NG.',
    canonicalPath: '/customer-dashboard',
    noindex: true,
  },
  pro_dashboard: {
    title: 'Professional Dashboard | JobMatch NG',
    description:
      'Manage matched job leads, submit itemized quotes, track active work milestones, and view earnings on JobMatch NG.',
    canonicalPath: '/professional-dashboard',
    noindex: true,
  },
  available_jobs: {
    title: 'Available Jobs for Verified Professionals | JobMatch NG',
    description:
      'Browse and quote on open customer service requests matched to your trade and Local Government Area on JobMatch NG.',
    canonicalPath: '/available-jobs',
    noindex: true,
  },
  admin_dashboard: {
    title: 'Administrator Portal | JobMatch NG',
    description: 'Restricted administrative portal for JobMatch NG operations and verification management.',
    canonicalPath: '/admin',
    noindex: true,
  },
  messages: {
    title: 'Messages | JobMatch NG',
    description:
      'Communicate securely with customers and verified professionals regarding your service jobs on JobMatch NG.',
    canonicalPath: '/messages',
    noindex: true,
  },
  notifications: {
    title: 'Notifications | JobMatch NG',
    description:
      'View real-time alerts for job matches, new quotes, work progress, and payment confirmations on JobMatch NG.',
    canonicalPath: '/notifications',
    noindex: true,
  },
  post_job: PUBLIC_PAGES_METADATA.post_job,
  quote_comparison: {
    title: 'Compare Artisan Quotes | JobMatch NG',
    description:
      'Compare itemized quotes, ratings, and verification badges from Nigerian professionals before hiring on JobMatch NG.',
    canonicalPath: '/quote-comparison',
    noindex: true,
  },
  pro_subscription: {
    title: 'Professional Plan & Billing | JobMatch NG',
    description: 'Manage your JobMatch NG professional account plan and billing details.',
    canonicalPath: '/pro-subscription',
    noindex: true,
  },
  pro_profile: {
    title: 'Verified Professional Profile | JobMatch NG',
    description:
      'View verified professional credentials, trade specialties, service coverage areas, and customer reviews on JobMatch NG.',
    canonicalPath: '/professional-profile',
    noindex: true,
  },
  customer_auth: PUBLIC_PAGES_METADATA.customer_auth,
  pro_auth: PUBLIC_PAGES_METADATA.pro_auth,
  admin_auth: {
    title: 'Administrator Sign In | JobMatch NG',
    description: 'Secure staff sign-in for JobMatch NG administration.',
    canonicalPath: '/admin_auth',
    noindex: true,
  },
  password_reset: {
    title: 'Reset Your Password | JobMatch NG',
    description: 'Securely reset the password for your JobMatch NG customer or professional account.',
    canonicalPath: '/reset-password',
    noindex: true,
  },
  not_found: {
    title: 'Page Not Found | JobMatch NG',
    description:
      'The requested page could not be found on JobMatch NG. Explore verified professionals and home services across Nigeria.',
    canonicalPath: '/',
    noindex: true,
  },
};

/**
 * Normalizes a canonical path so it always starts with '/' and has no trailing slash (except root '/')
 */
function normalizeCanonicalPath(rawPath?: string): string {
  if (!rawPath || typeof rawPath !== 'string') return '/';
  const trimmed = rawPath.trim();
  if (!trimmed || trimmed === '/') return '/';
  const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  // Preserve query string if explicitly part of custom canonical path, but strip trailing slash on pathname
  const [pathPart, queryPart] = withLeadingSlash.split('?');
  const cleanPath = pathPart.length > 1 ? pathPart.replace(/\/+$/, '') : '/';
  return queryPart ? `${cleanPath}?${queryPart}` : cleanPath;
}

/**
 * Ensures any dynamic title maintains the official JobMatch NG brand suffix
 */
function ensureBrandedTitle(rawTitle?: string): string {
  const clean = (rawTitle || '').trim();
  if (!clean) return SITE_CONFIG.defaultTitle;
  if (clean.toLowerCase().includes('jobmatch ng')) return clean;
  return `${clean} | ${SITE_CONFIG.name}`;
}

/**
 * Resolves page-specific SEO metadata, unique description, and canonical URL for a given view and parameters
 */
export function resolvePageSEO(
  view: AppView,
  customData?: Partial<PageMetadata>,
  viewParams?: ViewParams
): ResolvedPageSEO {
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname.toLowerCase().replace(/\/+$/, '') || '/' : '/';

  const hasCustomData = Boolean(customData && Object.keys(customData).length > 0);
  let resolvedMeta: Partial<PageMetadata> | undefined = hasCustomData ? customData : undefined;

  if (!resolvedMeta) {
    if (view === 'find_pro' || (view as string) === 'service_landing') {
      const pathSlug = pathname.startsWith('/services/') ? pathname.replace('/services/', '').trim() : '';
      const activeCategorySlug = (
        viewParams?.selectedCategory ||
        (viewParams as any)?.serviceSlug ||
        pathSlug ||
        ''
      )
        .toLowerCase()
        .trim();

      if (activeCategorySlug && SERVICE_PAGES_METADATA[activeCategorySlug]) {
        const baseServiceMeta = SERVICE_PAGES_METADATA[activeCategorySlug];
        const stateFilter = viewParams?.selectedState?.trim();
        const lgaFilter = viewParams?.selectedLga?.trim();

        if (stateFilter) {
          const locationLabel = lgaFilter ? `${lgaFilter}, ${stateFilter}` : stateFilter;
          const tradeLabel = baseServiceMeta.category || activeCategorySlug.replace(/-/g, ' ');
          resolvedMeta = {
            ...baseServiceMeta,
            title: `${tradeLabel} in ${locationLabel} – Hire Verified Artisans | JobMatch NG`,
            description: `Hire NIN-verified ${tradeLabel.toLowerCase()} professionals in ${locationLabel}, Nigeria. Compare itemized Naira quotes, view ratings, and pay safely via Paystack escrow on JobMatch NG.`,
            // Canonical path stays on the canonical trade service URL to prevent duplicate content from location filters
            canonicalPath: baseServiceMeta.canonicalPath,
          };
        } else {
          resolvedMeta = baseServiceMeta;
        }
      } else if (viewParams?.selectedState) {
        const locationLabel = viewParams.selectedLga
          ? `${viewParams.selectedLga}, ${viewParams.selectedState}`
          : viewParams.selectedState;
        resolvedMeta = {
          ...PUBLIC_PAGES_METADATA.find_pro,
          title: `Find Verified Artisans in ${locationLabel} | JobMatch NG`,
          description: `Search and hire NIN-verified plumbers, electricians, mechanics, and skilled artisans in ${locationLabel}, Nigeria. Compare quotes in Naira on JobMatch NG.`,
          canonicalPath: '/find-a-professional',
        };
      } else {
        resolvedMeta = PUBLIC_PAGES_METADATA.find_pro;
      }
    } else if (
      view === 'customer_auth' &&
      (viewParams?.authMode === 'register' || pathname === '/customer-register' || pathname === '/register')
    ) {
      resolvedMeta = PUBLIC_PAGES_METADATA.customer_register;
    } else if (
      view === 'pro_auth' &&
      (viewParams?.authMode === 'register' ||
        pathname === '/register-pro' ||
        pathname === '/pro-register' ||
        pathname === '/pro/register')
    ) {
      resolvedMeta = PUBLIC_PAGES_METADATA.pro_register;
    } else if (view === 'post_job' && viewParams?.selectedCategory) {
      const catSlug = viewParams.selectedCategory.toLowerCase().trim();
      const serviceMeta = SERVICE_PAGES_METADATA[catSlug];
      const tradeName = serviceMeta?.category || viewParams.selectedCategory;
      resolvedMeta = {
        ...PUBLIC_PAGES_METADATA.post_job,
        title: `Post a ${tradeName} Job Request in Nigeria | JobMatch NG`,
        description: `Request quotes from verified ${tradeName.toLowerCase()} professionals in Nigeria. Describe your repair project, get an AI scope diagnosis, and hire safely on JobMatch NG.`,
        canonicalPath: '/post-job',
      };
    } else if (view === 'pro_profile' && viewParams?.proId) {
      const proName = viewParams.targetProfessionalName || (viewParams as any)?.proName || 'Verified Artisan';
      resolvedMeta = {
        ...PRIVATE_PAGES_METADATA.pro_profile,
        title: `${proName} – Professional Artisan Profile | JobMatch NG`,
        description: `View ${proName}'s verified credentials, trade specialties, service coverage LGAs, diagnostic rates, and customer reviews on JobMatch NG.`,
        canonicalPath: `/professional-profile?proId=${encodeURIComponent(viewParams.proId)}`,
        noindex: true,
      };
    }
  }

  const baseFallback =
    PUBLIC_PAGES_METADATA[view] ||
    PRIVATE_PAGES_METADATA[view] ||
    PUBLIC_PAGES_METADATA.home;

  const mergedMeta: PageMetadata = {
    title: ensureBrandedTitle(resolvedMeta?.title || baseFallback.title || SITE_CONFIG.defaultTitle),
    description: (resolvedMeta?.description || baseFallback.description || SITE_CONFIG.defaultDescription).trim(),
    ogDescription: resolvedMeta?.ogDescription || baseFallback.ogDescription,
    canonicalPath: normalizeCanonicalPath(resolvedMeta?.canonicalPath || baseFallback.canonicalPath || '/'),
    noindex: resolvedMeta?.noindex !== undefined ? resolvedMeta.noindex : Boolean(baseFallback.noindex),
    ogType: resolvedMeta?.ogType || baseFallback.ogType || 'website',
    ogImage: resolvedMeta?.ogImage || baseFallback.ogImage || SITE_CONFIG.defaultOgImage,
    ogImageAlt: resolvedMeta?.ogImageAlt || baseFallback.ogImageAlt || SITE_CONFIG.defaultOgImageAlt,
    category: resolvedMeta?.category || baseFallback.category,
  };

  const canonicalBaseUrl = (SITE_CONFIG.productionUrl || 'https://jobmatch.ng').replace(/\/+$/, '');
  const canonicalUrl = `${canonicalBaseUrl}${mergedMeta.canonicalPath}`;
  const socialDescription =
    mergedMeta.ogDescription ||
    (view === 'home' && !customData ? SITE_CONFIG.defaultOgDescription : mergedMeta.description);

  return {
    ...mergedMeta,
    canonicalUrl,
    socialDescription,
    isNoIndex: Boolean(mergedMeta.noindex),
  };
}

/**
 * Updates DOM head elements dynamically on client-side SPA navigation
 */
export function updateDocumentSEO(
  view: AppView,
  customData?: Partial<PageMetadata>,
  viewParams?: ViewParams
): void {
  if (typeof document === 'undefined') return;

  const resolved = resolvePageSEO(view, customData, viewParams);

  // 1. Document Title
  document.title = resolved.title;

  // 2. Meta Description
  let metaDesc = document.querySelector('meta[name="description"]');
  if (!metaDesc) {
    metaDesc = document.createElement('meta');
    metaDesc.setAttribute('name', 'description');
    document.head.prepend(metaDesc);
  }
  metaDesc.setAttribute('content', resolved.description);

  // 3. Robots Meta Tag
  let metaRobots = document.querySelector('meta[name="robots"]');
  if (!metaRobots) {
    metaRobots = document.createElement('meta');
    metaRobots.setAttribute('name', 'robots');
    document.head.prepend(metaRobots);
  }
  if (resolved.isNoIndex) {
    metaRobots.setAttribute('content', 'noindex, nofollow');
  } else {
    metaRobots.setAttribute(
      'content',
      'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
    );
  }

  // 4. Canonical Link Tag
  let linkCanonical = document.querySelector('link[rel="canonical"]');
  if (!linkCanonical) {
    linkCanonical = document.createElement('link');
    linkCanonical.setAttribute('rel', 'canonical');
    document.head.prepend(linkCanonical);
  }
  linkCanonical.setAttribute('href', resolved.canonicalUrl);

  // 5. OpenGraph Tags
  const ogMap: Record<string, string> = {
    'og:type': resolved.ogType || 'website',
    'og:site_name': SITE_CONFIG.name,
    'og:title': resolved.title,
    'og:description': resolved.socialDescription,
    'og:url': resolved.canonicalUrl,
    'og:image': resolved.ogImage || SITE_CONFIG.defaultOgImage,
    'og:image:width': SITE_CONFIG.defaultOgImageWidth,
    'og:image:height': SITE_CONFIG.defaultOgImageHeight,
    'og:image:alt': resolved.ogImageAlt || SITE_CONFIG.defaultOgImageAlt,
    'og:locale': SITE_CONFIG.defaultLocale,
  };

  Object.entries(ogMap).forEach(([prop, val]) => {
    let tag = document.querySelector(`meta[property="${prop}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('property', prop);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', val);
  });

  // 6. Twitter / X Card Tags
  const twitterMap: Record<string, string> = {
    'twitter:card': 'summary_large_image',
    'twitter:title': resolved.title,
    'twitter:description': resolved.socialDescription,
    'twitter:image': resolved.ogImage || SITE_CONFIG.defaultOgImage,
    'twitter:url': resolved.canonicalUrl,
  };

  Object.entries(twitterMap).forEach(([name, val]) => {
    let tag = document.querySelector(`meta[name="${name}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', name);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', val);
  });
}
